import { DateTime } from 'luxon'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import translations from '@translationKeys'
import { Nullable } from '@types'

import CustomPopover from '@components/CustomPopover'
import TruncateLines from '@components/TruncateLines'
import DefaultCompany from '@components/icons/DefaultCompany'
import { EVENT_ACTIVITY_KEYS } from '@modules/activity/activityKeys'
import { useTrackUserActivity } from '@modules/activity/useTrackUserActivity'
import { POneEvent } from '@modules/auth/hooks/types'
import { useAuthContext } from '@modules/auth/hooks/useAuthContext'
import { ChatType, LocalChat } from '@modules/chat/types'
import useAttendees from '@modules/profile/hooks/useAttendees'
import useFetchProfile from '@modules/profile/hooks/useFetchProfile'
import { getCompanyId } from '@modules/search/components/ResultsSection/util/ResultProfile.util'
import { AnyResultProfile, Company, Delegate, ProfileType, isCompany } from '@modules/search/types'
import { Box, CircularProgress, Typography } from '@mui/material'
import { colors } from '@theme/variables'

import ContactedBubble from './ContactedBubble'
import { AttendeeAvatar, AttendeeItemWrapper } from './index.styles'

const ContactAttendeesPopover = ({
  anchor,
  profile,
  close,
  openChatModal,
}: {
  anchor?: Nullable<HTMLElement>
  close: () => void
  profile: AnyResultProfile
  openChatModal: (data: { chat?: LocalChat; delegates?: Delegate[]; companyId: Company['id']; chatType?: ChatType }) => void
}) => {
  const { user } = useAuthContext()
  const { t } = useTranslation()
  const companyId = getCompanyId(profile)

  const { attendees, eventData, isInitialLoading, hasContactedWithCompany } = useAttendees({
    companyId,
    eventId: profile.latest.eventData?.id,
  })
  const companyName = isCompany(profile) ? profile.latest.name : profile.latest.companyName
  const { data: companyProfile } = useFetchProfile({ type: ProfileType.company, id: companyId })

  const attendeesWithoutCurrentUser = useMemo(() => attendees.filter(attendee => attendee.id !== user?.id), [attendees, user])
  const { trackActivity } = useTrackUserActivity()

  return anchor ? (
    <CustomPopover
      id="chat-with-attendees-popover"
      anchor={anchor}
      setAnchor={close}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
    >
      <Box width="245px" maxHeight="320px" padding="16px">
        {isInitialLoading ? (
          <CircularProgress />
        ) : (
          <>
            <Box mb="10px">
              {!!attendees?.length && <Typography variant="caption">{t(translations.ATTENDEES_AT)}</Typography>}
              <Typography variant="body1">
                <TruncateLines lines={2}>{eventData?.name}</TruncateLines>
              </Typography>
            </Box>
            <AttendeeItemWrapper
              id="contact_button-company"
              onClick={() => {
                close()
                trackActivity(EVENT_ACTIVITY_KEYS.ATTENDEES_MODAL.CONTACT_BUTTON.CLICK, { type: 'company', id: companyId })
                openChatModal({
                  chat: {
                    id: -1,
                    name: companyName,
                    avatar: companyProfile?.latest?.logo,
                    type: ChatType.GROUP,
                    senderId: user?.id as number,
                    date: DateTime.now().toISO() || '',
                    withDelegates: attendeesWithoutCurrentUser.map(i => i.id),
                    withCompanyId: companyId,
                    eventData: eventData as POneEvent,
                  },
                  companyId,
                })
              }}
            >
              <ContactedBubble hasContacted={hasContactedWithCompany}>
                <AttendeeAvatar src={companyProfile?.latest?.logo} alt={companyName} sx={{ backgroundColor: colors.primary }}>
                  {!companyProfile?.latest?.logo && <DefaultCompany color="primary" fontSize="large" />}
                </AttendeeAvatar>
              </ContactedBubble>
              <Box ml="20px">
                <Typography variant="caption">{t(translations.SEND_TO_ALL)}</Typography>
                <Typography variant="body1">
                  <TruncateLines lines={2}>{t(translations.COMPANY_MESSAGE)}</TruncateLines>
                </Typography>
              </Box>
            </AttendeeItemWrapper>
            {attendeesWithoutCurrentUser.map(i => (
              <AttendeeItemWrapper
                key={i.id}
                id="contact_button-delegate"
                onClick={() => {
                  close()
                  trackActivity(EVENT_ACTIVITY_KEYS.ATTENDEES_MODAL.CONTACT_BUTTON.CLICK, { type: 'delegate', id: i.id })
                  openChatModal({ delegates: [i] as unknown as Delegate[], companyId, chatType: ChatType.DIRECT })
                }}
              >
                <ContactedBubble hasContacted={i.hasContacted}>
                  <AttendeeAvatar src={i.profilePicture} alt={`${i.lastName} ${i.firstName}`}>
                    {i?.firstName?.charAt(0)}
                    {i?.lastName?.charAt(0)}
                  </AttendeeAvatar>
                </ContactedBubble>
                <Box maxWidth="70%" ml="20px">
                  <Typography variant="caption">
                    <TruncateLines lines={2}>{`${i.firstName} ${i.lastName}`}</TruncateLines>
                  </Typography>
                  <Typography variant="body1">
                    <TruncateLines lines={2}>{i.jobTitle}</TruncateLines>
                  </Typography>
                </Box>
              </AttendeeItemWrapper>
            ))}
          </>
        )}
      </Box>
    </CustomPopover>
  ) : null
}

export default ContactAttendeesPopover
